import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

const ScraperDashboard = () => {
  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedColleges, setSelectedColleges] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('colleges'); // 'colleges', 'data-quality', 'queue', 'history', 'trigger'
  const [searchFilter, setSearchFilter] = useState('');
  const [qualityFilter, setQualityFilter] = useState('all'); // 'all', 'missing', 'improbable', 'complete'
  const [studentFilter, setStudentFilter] = useState('all'); // 'all', 'selected', 'unselected'
  const [dataQualityFilter, setDataQualityFilter] = useState('all'); // 'all', 'complete', 'partial', 'missing'
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [editingCollege, setEditingCollege] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    fetchColleges();
    fetchQueue();
    fetchHistory();

    // Subscribe to queue changes
    const queueSubscription = supabase
      .channel('scrape_queue_changes')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'scrape_queue' },
        () => fetchQueue()
      )
      .subscribe();

    return () => {
      queueSubscription.unsubscribe();
    };
  }, []);

  const fetchQueue = async () => {
    const { data, error } = await supabase
      .from('scrape_queue')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);

    if (!error) setQueue(data || []);
  };

  const fetchHistory = async () => {
    const { data, error } = await supabase
      .from('scrape_history')
      .select(`
        *,
        global_colleges (name)
      `)
      .order('created_at', { ascending: false })
      .limit(50);

    if (!error) setHistory(data || []);
  };

  const fetchColleges = async () => {
    // First, get list of college IDs that students have selected
    const { data: selectedCollegeIds, error: selectedError } = await supabase
      .from('college_journey_colleges')
      .select('global_college_id');

    const studentSelectedIds = new Set(
      (selectedCollegeIds || []).map(item => item.global_college_id)
    );

    // Then fetch all colleges with their data
    const { data, error } = await supabase
      .from('global_colleges')
      .select(`
        id, name, ed_deadline, ed2_deadline, ea_deadline, rd_deadline, finaid_deadline,
        data_source, last_scraped_at, manually_verified, quality_flags,
        acceptance_rate, sat_range_25th, sat_range_75th, act_range_25th, act_range_75th,
        setting, total_undergrad, student_faculty_ratio, graduation_rate, retention_rate, majors_available,
        housing_cost_per_year,
        out_of_state_pct, african_american_pct, asian_pct, hispanic_pct, white_pct,
        scraper_last_updated, bigfuture_url
      `)
      .order('name', { ascending: true });

    if (!error) {
      // Add quality analysis and student-selected flag to each college
      const analyzed = (data || []).map(college => ({
        ...college,
        isStudentSelected: studentSelectedIds.has(college.id),
        qualityIssues: analyzeCollegeQuality(college)
      }));
      setColleges(analyzed);
    }
  };

  // Analyze college data quality
  const analyzeCollegeQuality = (college) => {
    const issues = [];

    // Check for missing deadlines
    if (!college.ed_deadline && !college.ea_deadline) {
      issues.push({ type: 'missing', severity: 'warning', field: 'Early Deadlines', message: 'No ED or EA deadline found' });
    }
    if (!college.rd_deadline) {
      issues.push({ type: 'missing', severity: 'error', field: 'RD Deadline', message: 'No Regular Decision deadline found' });
    }

    // Check for improbable dates
    const checkImprobableDate = (deadline, name, expectedMonths) => {
      if (!deadline) return;
      try {
        const [year, month, day] = deadline.split('-').map(Number);
        if (!expectedMonths.includes(month)) {
          issues.push({
            type: 'improbable',
            severity: 'warning',
            field: name,
            message: `${name} is ${deadline} (expected months: ${expectedMonths.map(m => new Date(2000, m - 1).toLocaleString('en', { month: 'short' })).join(', ')})`,
            value: deadline
          });
        }
      } catch (e) {
        // Invalid date format
      }
    };

    checkImprobableDate(college.ed_deadline, 'ED', [10, 11]); // Oct-Nov
    checkImprobableDate(college.ed2_deadline, 'ED II', [1, 2]); // Jan-Feb
    checkImprobableDate(college.ea_deadline, 'EA', [10, 11]); // Oct-Nov
    checkImprobableDate(college.rd_deadline, 'RD', [12, 1, 2]); // Dec-Feb
    checkImprobableDate(college.finaid_deadline, 'Financial Aid', [1, 2, 3, 4]); // Jan-Apr

    return issues;
  };

  // Analyze comprehensive data completeness
  const analyzeDataCompleteness = (college) => {
    const fields = {
      admissions: ['acceptance_rate', 'sat_range_25th', 'sat_range_75th', 'act_range_25th', 'act_range_75th'],
      profile: ['setting', 'total_undergrad', 'student_faculty_ratio', 'graduation_rate', 'retention_rate', 'majors_available'],
      costs: ['housing_cost_per_year'],
      demographics: ['out_of_state_pct', 'african_american_pct', 'asian_pct', 'hispanic_pct', 'white_pct']
    };

    const completeness = {};
    let totalFields = 0;
    let filledFields = 0;

    Object.keys(fields).forEach(category => {
      const categoryFields = fields[category];
      const filled = categoryFields.filter(field => college[field] != null).length;
      completeness[category] = {
        filled,
        total: categoryFields.length,
        percentage: (filled / categoryFields.length) * 100
      };
      totalFields += categoryFields.length;
      filledFields += filled;
    });

    completeness.overall = {
      filled: filledFields,
      total: totalFields,
      percentage: (filledFields / totalFields) * 100
    };

    return completeness;
  };

  // Filter colleges based on search, quality, and student selection filters
  const getFilteredColleges = () => {
    let filtered = colleges;

    // Search filter
    if (searchFilter) {
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(searchFilter.toLowerCase())
      );
    }

    // Student selection filter
    if (studentFilter !== 'all') {
      filtered = filtered.filter(c => {
        if (studentFilter === 'selected') {
          return c.isStudentSelected;
        } else if (studentFilter === 'unselected') {
          return !c.isStudentSelected;
        }
        return true;
      });
    }

    // Quality filter
    if (qualityFilter !== 'all') {
      filtered = filtered.filter(c => {
        if (qualityFilter === 'missing') {
          return c.qualityIssues.some(i => i.type === 'missing');
        } else if (qualityFilter === 'improbable') {
          return c.qualityIssues.some(i => i.type === 'improbable');
        } else if (qualityFilter === 'complete') {
          return c.qualityIssues.length === 0;
        }
        return true;
      });
    }

    return filtered;
  };

  // Toggle college selection for batch scraping
  const toggleCollegeSelection = (collegeId) => {
    const newSelection = new Set(selectedColleges);
    if (newSelection.has(collegeId)) {
      newSelection.delete(collegeId);
    } else {
      newSelection.add(collegeId);
    }
    setSelectedColleges(newSelection);
  };

  // Select all filtered colleges
  const selectAllFiltered = () => {
    const filtered = getFilteredColleges();
    const newSelection = new Set(filtered.map(c => c.id));
    setSelectedColleges(newSelection);
  };

  // Generate batch scrape commands
  const generateBatchCommands = () => {
    const selected = colleges.filter(c => selectedColleges.has(c.id));
    return selected.map(c => `./api/scraper/run-scraper-local.sh "${c.name}"`).join('\n');
  };

  // Toggle row expansion
  const toggleRowExpansion = (collegeId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(collegeId)) {
      newExpanded.delete(collegeId);
    } else {
      newExpanded.add(collegeId);
    }
    setExpandedRows(newExpanded);
  };

  // Start editing a college
  const startEditing = (college) => {
    setEditingCollege(college.id);
    setEditFormData({
      acceptance_rate: college.acceptance_rate || '',
      sat_range_25th: college.sat_range_25th || '',
      sat_range_75th: college.sat_range_75th || '',
      act_range_25th: college.act_range_25th || '',
      act_range_75th: college.act_range_75th || '',
      setting: college.setting || '',
      total_undergrad: college.total_undergrad || '',
      student_faculty_ratio: college.student_faculty_ratio || '',
      graduation_rate: college.graduation_rate || '',
      retention_rate: college.retention_rate || '',
      majors_available: college.majors_available || '',
      housing_cost_per_year: college.housing_cost_per_year || '',
      out_of_state_pct: college.out_of_state_pct || '',
      african_american_pct: college.african_american_pct || '',
      asian_pct: college.asian_pct || '',
      hispanic_pct: college.hispanic_pct || '',
      white_pct: college.white_pct || '',
    });
  };

  // Save edited college
  const saveEdit = async (collegeId) => {
    try {
      const updateData = { ...editFormData };

      // Convert percentages from 0-100 format to 0-1 decimals
      ['acceptance_rate', 'graduation_rate', 'retention_rate', 'out_of_state_pct',
       'african_american_pct', 'asian_pct', 'hispanic_pct', 'white_pct'].forEach(field => {
        if (updateData[field] !== '' && updateData[field] > 1) {
          updateData[field] = updateData[field] / 100;
        }
      });

      // Convert empty strings to null
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === '') updateData[key] = null;
      });

      await supabase
        .table('global_colleges')
        .update(updateData)
        .eq('id', collegeId);

      setEditingCollege(null);
      fetchColleges(); // Refresh data
    } catch (error) {
      console.error('Error saving college:', error);
      alert('Failed to save changes');
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingCollege(null);
    setEditFormData({});
  };

  // Note: Scraper runs locally only due to Vercel's 250MB serverless function limit
  // Use: ./api/scraper/run-scraper-local.sh "School Name"

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      running: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      error: 'bg-red-100 text-red-800',
      warning: 'bg-yellow-100 text-yellow-800',
      info: 'bg-blue-100 text-blue-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[severity] || 'bg-gray-100 text-gray-800'}`}>
        {severity}
      </span>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Scraper Dashboard</h1>
        <p className="text-gray-600 mt-2">BigFuture-first college data scraper management</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'colleges', label: 'Deadlines' },
            { id: 'data-quality', label: 'Data Quality' },
            { id: 'queue', label: 'Queue' },
            { id: 'history', label: 'History' },
            { id: 'trigger', label: 'Manual Trigger' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Colleges Tab */}
      {activeTab === 'colleges' && (
        <div className="space-y-4">
          {/* Filters and Actions */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              {/* Search */}
              <input
                type="text"
                placeholder="Search colleges..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
              />

              {/* Student Selection Filter */}
              <select
                value={studentFilter}
                onChange={(e) => setStudentFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50"
              >
                <option value="all">All Colleges</option>
                <option value="selected">⭐ Student-Selected ({colleges.filter(c => c.isStudentSelected).length})</option>
                <option value="unselected">Not Selected ({colleges.filter(c => !c.isStudentSelected).length})</option>
              </select>

              {/* Quality Filter */}
              <select
                value={qualityFilter}
                onChange={(e) => setQualityFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Quality</option>
                <option value="missing">Missing Deadlines ({colleges.filter(c => c.qualityIssues.some(i => i.type === 'missing')).length})</option>
                <option value="improbable">Improbable Dates ({colleges.filter(c => c.qualityIssues.some(i => i.type === 'improbable')).length})</option>
                <option value="complete">Complete Data ({colleges.filter(c => c.qualityIssues.length === 0).length})</option>
              </select>

              {/* Batch Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const studentSelected = colleges.filter(c => c.isStudentSelected).map(c => c.id);
                    setSelectedColleges(new Set(studentSelected));
                  }}
                  className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                  title="Select all colleges that students have added to their lists"
                >
                  ⭐ Select Student Colleges ({colleges.filter(c => c.isStudentSelected).length})
                </button>
                <button
                  onClick={selectAllFiltered}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                >
                  Select All Filtered ({getFilteredColleges().length})
                </button>
                <button
                  onClick={() => setSelectedColleges(new Set())}
                  disabled={selectedColleges.size === 0}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm disabled:opacity-50"
                >
                  Clear Selection
                </button>
              </div>
            </div>

            {/* Batch Commands Display */}
            {selectedColleges.size > 0 && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium text-blue-900">{selectedColleges.size} College{selectedColleges.size !== 1 ? 's' : ''} Selected</h3>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(generateBatchCommands());
                      alert('Batch commands copied to clipboard!');
                    }}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    Copy Batch Commands
                  </button>
                </div>
                <div className="bg-gray-900 text-green-400 rounded px-3 py-2 font-mono text-xs overflow-x-auto max-h-40 overflow-y-auto">
                  {generateBatchCommands()}
                </div>
                <p className="text-xs text-blue-700 mt-2">
                  💡 Tip: Run these commands in sequence, or create a shell script to batch process
                </p>
              </div>
            )}
          </div>

          {/* Colleges Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      <input
                        type="checkbox"
                        checked={getFilteredColleges().length > 0 && getFilteredColleges().every(c => selectedColleges.has(c.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            selectAllFiltered();
                          } else {
                            setSelectedColleges(new Set());
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">College</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ED</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ED II</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">EA</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RD</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fin Aid</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quality</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getFilteredColleges().map((college) => (
                    <tr key={college.id} className={`hover:bg-gray-50 ${selectedColleges.has(college.id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedColleges.has(college.id)}
                          onChange={() => toggleCollegeSelection(college.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900">{college.name}</div>
                          {college.isStudentSelected && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" title="Selected by students">
                              ⭐ Student
                            </span>
                          )}
                          {college.manually_verified && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              ✓ Verified
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {college.ed_deadline ? (
                          <span className={college.qualityIssues.find(i => i.field === 'ED') ? 'text-yellow-600' : ''}>
                            {new Date(college.ed_deadline + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {college.ed2_deadline ? (
                          <span className={college.qualityIssues.find(i => i.field === 'ED II') ? 'text-yellow-600' : ''}>
                            {new Date(college.ed2_deadline + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {college.ea_deadline ? (
                          <span className={college.qualityIssues.find(i => i.field === 'EA') ? 'text-yellow-600' : ''}>
                            {new Date(college.ea_deadline + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {college.rd_deadline ? (
                          <span className={college.qualityIssues.find(i => i.field === 'RD') ? 'text-yellow-600' : ''}>
                            {new Date(college.rd_deadline + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        ) : <span className="text-red-600 font-medium">Missing</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {college.finaid_deadline ? (
                          <span className={college.qualityIssues.find(i => i.field === 'Financial Aid') ? 'text-yellow-600' : ''}>
                            {new Date(college.finaid_deadline + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {college.qualityIssues.length === 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            ✓ Good
                          </span>
                        ) : (
                          <div className="flex flex-col gap-1">
                            {college.qualityIssues.map((issue, idx) => (
                              <span
                                key={idx}
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  issue.type === 'missing' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                }`}
                                title={issue.message}
                              >
                                {issue.type === 'missing' ? '⚠ Missing' : '⚡ Improbable'}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex flex-col">
                          <span className="capitalize">{college.data_source || 'unknown'}</span>
                          {college.last_scraped_at && (
                            <span className="text-xs text-gray-400">
                              {new Date(college.last_scraped_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => {
                            setSelectedSchool(college.name);
                            setActiveTab('trigger');
                          }}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Scrape →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {getFilteredColleges().length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No colleges match the current filters
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manual Trigger Tab */}
      {activeTab === 'trigger' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Manual Scrape Trigger</h2>

          {/* Local-Only Notice */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="text-yellow-600 text-xl">⚠️</div>
              <div>
                <h3 className="font-medium text-yellow-900 mb-1">Scraper Runs Locally Only</h3>
                <p className="text-sm text-yellow-800 mb-2">
                  Due to Vercel's 250 MB serverless function limit, the BigFuture scraper runs on your local machine, not in production.
                </p>
                <div className="bg-yellow-100 rounded px-3 py-2 text-xs font-mono text-yellow-900 mt-2">
                  ./api/scraper/run-scraper-local.sh "Johns Hopkins University"
                </div>
                <p className="text-xs text-yellow-700 mt-2">
                  Results are saved to <code className="bg-yellow-100 px-1 rounded">scrape_history</code> table and will be visible in the History tab below.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                School Name (for reference)
              </label>
              <input
                type="text"
                value={selectedSchool}
                onChange={(e) => setSelectedSchool(e.target.value)}
                placeholder="e.g., Johns Hopkins University"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Run from Terminal:</h4>
              <div className="bg-gray-900 text-green-400 rounded px-3 py-2 font-mono text-sm">
                cd ~/Developer/PaperSedge/papersedge<br/>
                ./api/scraper/run-scraper-local.sh "{selectedSchool || 'College Name'}"
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Quick Test Schools</h3>
              <div className="flex flex-wrap gap-2">
                {['Johns Hopkins University', 'Duke University', 'Stanford University', 'Yale University', 'Rice University'].map((school) => (
                  <button
                    key={school}
                    onClick={() => setSelectedSchool(school)}
                    className="px-3 py-1 bg-white border border-blue-300 rounded-md hover:bg-blue-50 text-sm"
                  >
                    {school}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Quality Tab */}
      {activeTab === 'data-quality' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              {/* Search */}
              <input
                type="text"
                placeholder="Search colleges..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
              />

              {/* Student Selection Filter */}
              <select
                value={studentFilter}
                onChange={(e) => setStudentFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50"
              >
                <option value="all">All Colleges</option>
                <option value="selected">⭐ Student-Selected ({colleges.filter(c => c.isStudentSelected).length})</option>
                <option value="unselected">Not Selected ({colleges.filter(c => !c.isStudentSelected).length})</option>
              </select>

              {/* Data Completeness Filter */}
              <select
                value={dataQualityFilter}
                onChange={(e) => setDataQualityFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Data Quality</option>
                <option value="complete">Complete (≥80%)</option>
                <option value="partial">Partial (20-79%)</option>
                <option value="missing">Missing (&lt;20%)</option>
              </select>
            </div>
          </div>

          {/* Batch Commands Display */}
          {selectedColleges.size > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-medium text-blue-900">{selectedColleges.size} College{selectedColleges.size !== 1 ? 's' : ''} Selected</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const commands = generateBatchCommands();
                      navigator.clipboard.writeText(commands);
                      alert('✓ Commands copied to clipboard!\n\nNext steps:\n1. Open Terminal (⌘+Space, type "Terminal")\n2. Navigate to project: cd ~/Developer/PaperSedge/papersedge\n3. Paste commands (⌘+V)\n4. Press Enter to run');
                    }}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center gap-1"
                  >
                    📋 Copy & Get Instructions
                  </button>
                  <button
                    onClick={() => {
                      const commandsWithSave = colleges
                        .filter(c => selectedColleges.has(c.id))
                        .map(c => `python api/scraper/bigfuture_harvester.py --school "${c.name}" --save`)
                        .join('\n');
                      navigator.clipboard.writeText(commandsWithSave);
                      alert('✓ Commands with --save flag copied!\n\nThese will scrape AND save to database.');
                    }}
                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center gap-1"
                  >
                    💾 Copy with --save
                  </button>
                </div>
              </div>
              <pre className="text-xs bg-white p-3 rounded border border-blue-200 overflow-x-auto max-h-48 overflow-y-auto">
                {generateBatchCommands()}
              </pre>
              <div className="mt-2 p-2 bg-blue-100 rounded text-xs">
                <p className="font-medium text-blue-900 mb-1">🚀 Quick Start:</p>
                <ol className="list-decimal ml-4 text-blue-800 space-y-1">
                  <li>Click "Copy with --save" button above</li>
                  <li>Open Terminal: <code className="bg-white px-1 rounded">⌘+Space</code> → type "Terminal"</li>
                  <li>Navigate: <code className="bg-white px-1 rounded">cd ~/Developer/PaperSedge/papersedge</code></li>
                  <li>Activate venv: <code className="bg-white px-1 rounded">source venv/bin/activate</code></li>
                  <li>Paste and run: <code className="bg-white px-1 rounded">⌘+V</code> then Enter</li>
                </ol>
              </div>
            </div>
          )}

          {/* Data Quality Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={getFilteredColleges().every(c => selectedColleges.has(c.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            selectAllFiltered();
                          } else {
                            setSelectedColleges(new Set());
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">College</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overall</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admissions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profile</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Costs</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Demographics</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getFilteredColleges()
                    .filter(c => {
                      if (dataQualityFilter === 'all') return true;
                      const completeness = analyzeDataCompleteness(c);
                      if (dataQualityFilter === 'complete') return completeness.overall.percentage >= 80;
                      if (dataQualityFilter === 'partial') return completeness.overall.percentage >= 20 && completeness.overall.percentage < 80;
                      if (dataQualityFilter === 'missing') return completeness.overall.percentage < 20;
                      return true;
                    })
                    .map((college) => {
                      const completeness = analyzeDataCompleteness(college);

                      const getProgressColor = (percentage) => {
                        if (percentage >= 80) return 'bg-green-500';
                        if (percentage >= 50) return 'bg-yellow-500';
                        if (percentage >= 20) return 'bg-orange-500';
                        return 'bg-red-500';
                      };

                      const ProgressBar = ({ percentage, filled, total }) => (
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${getProgressColor(percentage)}`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">{filled}/{total}</span>
                        </div>
                      );

                      const isExpanded = expandedRows.has(college.id);
                      const isEditing = editingCollege === college.id;

                      return (
                        <React.Fragment key={college.id}>
                          <tr className={`hover:bg-gray-50 ${selectedColleges.has(college.id) ? 'bg-blue-50' : ''}`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedColleges.has(college.id)}
                                onChange={() => toggleCollegeSelection(college.id)}
                                className="rounded border-gray-300"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleRowExpansion(college.id)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  {isExpanded ? '▼' : '▶'}
                                </button>
                                <div className="text-sm font-medium text-gray-900">{college.name}</div>
                                {college.isStudentSelected && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    ⭐
                                  </span>
                                )}
                              </div>
                            </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <ProgressBar
                              percentage={completeness.overall.percentage}
                              filled={completeness.overall.filled}
                              total={completeness.overall.total}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <ProgressBar
                              percentage={completeness.admissions.percentage}
                              filled={completeness.admissions.filled}
                              total={completeness.admissions.total}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <ProgressBar
                              percentage={completeness.profile.percentage}
                              filled={completeness.profile.filled}
                              total={completeness.profile.total}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <ProgressBar
                              percentage={completeness.costs.percentage}
                              filled={completeness.costs.filled}
                              total={completeness.costs.total}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <ProgressBar
                              percentage={completeness.demographics.percentage}
                              filled={completeness.demographics.filled}
                              total={completeness.demographics.total}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {college.scraper_last_updated
                              ? new Date(college.scraper_last_updated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : '-'
                            }
                          </td>
                        </tr>

                        {/* Expanded Detail Row */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="8" className="px-6 py-4 bg-gray-50">
                              <div className="space-y-4">
                                {/* Header with Edit Button */}
                                <div className="flex justify-between items-center">
                                  <h4 className="font-semibold text-gray-900">Detailed Data</h4>
                                  <div className="flex gap-2">
                                    {!isEditing ? (
                                      <button
                                        onClick={() => startEditing(college)}
                                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                                      >
                                        ✏️ Edit
                                      </button>
                                    ) : (
                                      <>
                                        <button
                                          onClick={() => saveEdit(college.id)}
                                          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                                        >
                                          ✓ Save
                                        </button>
                                        <button
                                          onClick={cancelEdit}
                                          className="px-3 py-1 bg-gray-400 text-white rounded text-sm hover:bg-gray-500"
                                        >
                                          ✕ Cancel
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Data Source Info */}
                                <div className="text-sm">
                                  <span className="font-medium">Source:</span>{' '}
                                  <span className="text-gray-600">{college.data_source || 'unknown'}</span>
                                  {college.bigfuture_url && (
                                    <> • <a href={college.bigfuture_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                      BigFuture URL
                                    </a></>
                                  )}
                                </div>

                                {/* Data Grid */}
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                  {/* Admissions Stats */}
                                  <div className="col-span-full">
                                    <h5 className="font-medium text-gray-700 mb-2">Admissions Stats</h5>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                      {[
                                        { key: 'acceptance_rate', label: 'Acceptance Rate', format: (v) => v ? `${(v * 100).toFixed(1)}%` : '-', inputType: 'number', step: 0.01 },
                                        { key: 'sat_range_25th', label: 'SAT 25th', format: (v) => v || '-', inputType: 'number' },
                                        { key: 'sat_range_75th', label: 'SAT 75th', format: (v) => v || '-', inputType: 'number' },
                                        { key: 'act_range_25th', label: 'ACT 25th', format: (v) => v || '-', inputType: 'number' },
                                        { key: 'act_range_75th', label: 'ACT 75th', format: (v) => v || '-', inputType: 'number' },
                                      ].map(field => (
                                        <div key={field.key} className="text-sm">
                                          <span className="text-gray-600">{field.label}:</span>{' '}
                                          {isEditing ? (
                                            <input
                                              type={field.inputType}
                                              step={field.step}
                                              value={editFormData[field.key]}
                                              onChange={(e) => setEditFormData({ ...editFormData, [field.key]: e.target.value })}
                                              className="ml-1 px-2 py-1 border rounded w-20"
                                            />
                                          ) : (
                                            <span className="font-medium">{field.format(college[field.key])}</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  {/* School Profile */}
                                  <div className="col-span-full">
                                    <h5 className="font-medium text-gray-700 mb-2">School Profile</h5>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                      {[
                                        { key: 'setting', label: 'Setting', format: (v) => v || '-', inputType: 'text' },
                                        { key: 'total_undergrad', label: 'Total Undergrad', format: (v) => v ? v.toLocaleString() : '-', inputType: 'number' },
                                        { key: 'student_faculty_ratio', label: 'Student:Faculty', format: (v) => v || '-', inputType: 'text' },
                                        { key: 'graduation_rate', label: 'Graduation Rate', format: (v) => v ? `${(v * 100).toFixed(1)}%` : '-', inputType: 'number', step: 0.01 },
                                        { key: 'retention_rate', label: 'Retention Rate', format: (v) => v ? `${(v * 100).toFixed(1)}%` : '-', inputType: 'number', step: 0.01 },
                                        { key: 'majors_available', label: 'Majors Available', format: (v) => v || '-', inputType: 'number' },
                                      ].map(field => (
                                        <div key={field.key} className="text-sm">
                                          <span className="text-gray-600">{field.label}:</span>{' '}
                                          {isEditing ? (
                                            <input
                                              type={field.inputType}
                                              step={field.step}
                                              value={editFormData[field.key]}
                                              onChange={(e) => setEditFormData({ ...editFormData, [field.key]: e.target.value })}
                                              className="ml-1 px-2 py-1 border rounded w-20"
                                            />
                                          ) : (
                                            <span className="font-medium">{field.format(college[field.key])}</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Costs & Demographics */}
                                  <div className="col-span-full">
                                    <h5 className="font-medium text-gray-700 mb-2">Costs & Demographics</h5>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                      {[
                                        { key: 'housing_cost_per_year', label: 'Housing Cost/Year', format: (v) => v ? `$${v.toLocaleString()}` : '-', inputType: 'number' },
                                        { key: 'out_of_state_pct', label: 'Out-of-State %', format: (v) => v ? `${(v * 100).toFixed(1)}%` : '-', inputType: 'number', step: 0.01 },
                                        { key: 'african_american_pct', label: 'African American %', format: (v) => v ? `${(v * 100).toFixed(1)}%` : '-', inputType: 'number', step: 0.01 },
                                        { key: 'asian_pct', label: 'Asian %', format: (v) => v ? `${(v * 100).toFixed(1)}%` : '-', inputType: 'number', step: 0.01 },
                                        { key: 'hispanic_pct', label: 'Hispanic %', format: (v) => v ? `${(v * 100).toFixed(1)}%` : '-', inputType: 'number', step: 0.01 },
                                        { key: 'white_pct', label: 'White %', format: (v) => v ? `${(v * 100).toFixed(1)}%` : '-', inputType: 'number', step: 0.01 },
                                      ].map(field => (
                                        <div key={field.key} className="text-sm">
                                          <span className="text-gray-600">{field.label}:</span>{' '}
                                          {isEditing ? (
                                            <input
                                              type={field.inputType}
                                              step={field.step}
                                              value={editFormData[field.key]}
                                              onChange={(e) => setEditFormData({ ...editFormData, [field.key]: e.target.value })}
                                              className="ml-1 px-2 py-1 border rounded w-20"
                                            />
                                          ) : (
                                            <span className="font-medium">{field.format(college[field.key])}</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Queue Tab */}
      {activeTab === 'queue' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">Scrape Queue</h2>
            <p className="text-sm text-gray-600 mt-1">
              {queue.filter(j => j.status === 'pending').length} pending,
              {queue.filter(j => j.status === 'running').length} running,
              {queue.filter(j => j.status === 'completed').length} completed
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">School</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Result</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {queue.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{job.school_name}</div>
                      {job.dry_run && <span className="text-xs text-gray-500">(Dry Run)</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(job.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.priority}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(job.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.duration_ms ? `${(job.duration_ms / 1000).toFixed(1)}s` : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {job.result_summary ? (
                        <div className="space-y-1">
                          <div>Deadlines: {job.result_summary.deadlines_found || 0}</div>
                          <div>Confidence: {((job.result_summary.confidence || 0) * 100).toFixed(0)}%</div>
                          {job.result_summary.quality_flags > 0 && (
                            <div className="text-yellow-600">
                              {job.result_summary.quality_flags} flags
                            </div>
                          )}
                        </div>
                      ) : job.error_message ? (
                        <div className="text-red-600">{job.error_message}</div>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          {history.map((entry) => (
            <div key={entry.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold">{entry.school_name}</h3>
                  <p className="text-sm text-gray-500">
                    Scraped {new Date(entry.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  {getStatusBadge(entry.scrape_status)}
                  <div className="text-sm text-gray-500 mt-1">
                    v{entry.scraper_version}
                  </div>
                </div>
              </div>

              {/* Deadlines */}
              {entry.deadlines && entry.deadlines.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium mb-2">Deadlines Found:</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {entry.deadlines.map((d, idx) => (
                      <div key={idx} className="bg-gray-50 p-3 rounded">
                        <div className="font-medium">{d.plan}</div>
                        <div className="text-sm text-gray-600">{d.date_iso}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Confidence: {(d.confidence * 100).toFixed(0)}%
                        </div>
                        <a
                          href={d.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Source →
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Essay Page */}
              {entry.essays?.url && (
                <div className="mb-4">
                  <h4 className="font-medium mb-2">Essay Page:</h4>
                  <div className="bg-gray-50 p-3 rounded">
                    <a
                      href={entry.essays.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm"
                    >
                      {entry.essays.url}
                    </a>
                    <div className="text-xs text-gray-500 mt-1">
                      Confidence: {(entry.essays.confidence * 100).toFixed(0)}%
                    </div>
                  </div>
                </div>
              )}

              {/* Quality Flags */}
              {entry.quality_flags && entry.quality_flags.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Quality Flags:</h4>
                  <div className="space-y-2">
                    {entry.quality_flags.map((flag, idx) => (
                      <div key={idx} className="flex items-start gap-2 bg-yellow-50 p-3 rounded">
                        {getSeverityBadge(flag.severity)}
                        <div className="flex-1">
                          <div className="font-medium text-sm">{flag.field}</div>
                          <div className="text-sm text-gray-600">{flag.message}</div>
                          {flag.bigfuture && flag.official && (
                            <div className="mt-2 text-xs">
                              <div>BigFuture: {JSON.stringify(flag.bigfuture)}</div>
                              <div>Official: {JSON.stringify(flag.official)}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScraperDashboard;
